import { LoadingOutlined, SearchOutlined } from "@ant-design/icons"
import { Divider, Flex, Select, type SelectProps, Typography } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import React, { useState } from "react"

import { getProjectTypes } from "../../containers/Templates/services/project-types.services"
import { type ProjectTypeTemplate } from "../../containers/Templates/types/project-type.types.ts"
import { useDebouncedCallback } from "../../hooks/use-debounced-callback.ts"
import { hideEmptyOverflow } from "../../utils/select/max-tag-placeholder.tsx"
import { showErrorNotifications } from "../../utils/handlers/handle-error"

const PAGE_SIZE = 20

type OptionType = BaseOptionType & { data: ProjectTypeTemplate }

export interface ProjectTypeSelectProps
  extends Omit<SelectProps, "options" | "loading" | "onChange" | "mode"> {
  initialOptions?: BaseOptionType[]
  onChange?: (ids: string[], option?: OptionType | OptionType[]) => void
  mode?: "multiple" | "tags" | null
}

export function ProjectTypeSelect({
  onChange,
  mode = "multiple",
  initialOptions = [],
  ...props
}: ProjectTypeSelectProps) {
  const [search, setSearch] = useState<string>("")

  const [options, setOptions] = useState<BaseOptionType[]>(initialOptions)
  const [loading, setLoading] = useState<boolean>(false)
  const [total, setTotal] = useState<number>(0)
  const [page, setPage] = useState<number>(1)
  const [hasMore, setHasMore] = useState<boolean>(true)

  const debounced = useDebouncedCallback((search: string) => {
    setPage(1)
    setSearch(search)
    void getProjectTypesHandler(search, 1, false)
  }, 700)

  const getProjectTypesHandler = async (
    q: string,
    currentPage: number,
    isLoadMore: boolean = false
  ) => {
    try {
      setLoading(true)

      const res = await getProjectTypes({
        q,
        page: currentPage,
        page_size: PAGE_SIZE
      })

      if (!res?.ok) throw new Error(res?.data?.details?.message)

      const data = res?.data
      const projectTypes = data?.results || []
      const total = data?.count || 0

      setTotal(total)
      setHasMore(!!data?.next)

      const newOptions = projectTypes.map((type: ProjectTypeTemplate) => ({
        value: type?.id,
        label: type?.name,
        data: type
      }))

      if (isLoadMore) {
        setOptions((prev) => [...prev, ...newOptions])
      } else {
        setOptions(newOptions)
      }
    } catch (e) {
      showErrorNotifications(e)
    } finally {
      setLoading(false)
    }
  }

  const onChangeHandler: SelectProps["onChange"] = (ids: string[], objects) => {
    onChange?.(ids, objects as OptionType | OptionType[])
  }

  const onPopupScroll: SelectProps["onPopupScroll"] = (e) => {
    const { target } = e
    const element = target as HTMLElement

    const isBottom =
      element.scrollTop + element.offsetHeight >= element.scrollHeight - 5

    if (isBottom && hasMore && !loading) {
      const nextPage = page + 1
      setPage(nextPage)
      void getProjectTypesHandler(search, nextPage, true)
    }
  }

  /*  useEffect(() => {
    setPage(1)
    void getProjectTypesHandler(1, false)
  }, [debounced])*/

  return (
    <Select
      showSearch
      onSearch={debounced}
      onChange={onChangeHandler}
      onPopupScroll={onPopupScroll}
      onOpenChange={(visible) => {
        if (visible && !loading) {
          setSearch("")
          setPage(1)
          void getProjectTypesHandler("", 1, false)
        }
      }}
      mode={mode ? mode : undefined}
      filterOption={false}
      loading={loading}
      optionLabelProp="label"
      placeholder="Search project types"
      options={options}
      maxTagCount={"responsive"}
      maxTagPlaceholder={hideEmptyOverflow}
      className={"w-full"}
      suffixIcon={loading ? <LoadingOutlined /> : <SearchOutlined />}
      notFoundContent={loading ? "Searching..." : "No project types found"}
      popupRender={(menu) => (
        <>
          {menu}

          {!!options?.length && (
            <>
              <Divider style={{ margin: "8px 0" }} />

              <Flex className={"p-2"} gap={"small"} justify={"center"}>
                {loading ? (
                  <>
                    <Typography.Text>Loading...</Typography.Text>
                    <LoadingOutlined />
                  </>
                ) : (
                  <Flex vertical align={"center"}>
                    <Typography.Text>
                      {search && `Search — ${search}`}
                    </Typography.Text>
                    <Typography.Text>
                      {total} project type{total !== 1 ? "s" : ""} found
                      {hasMore && " — scroll for more"}
                    </Typography.Text>
                  </Flex>
                )}
              </Flex>
            </>
          )}
        </>
      )}
      {...props}
    />
  )
}
